import React from 'react'
import PropTypes from 'prop-types'
import { Message, Radio, Input } from 'semantic-ui-react'
import { State } from 'react-powerplug'
import debounce from 'lodash/debounce'
import styled from 'styled-components'

import useFeatureToggle from 'hooks/useFeatureToggle'
import { AutoFocusSemanticUiComponent } from '../AutoFocusSemanticUiComponent'
import AudienceRuleAutoCompleteInputContainer from 'components/AudienceRuleAutoCompleteInput/container'
import {
  RelativeDateInput,
  parseRelativeDateValue,
  serializeRelativeDateValue,
  isValidRelativeDateValue,
} from './RelativeDateInput'

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 0.5em;
  padding-bottom: 0.5em;

  > div:not(:first-child) {
    margin-top: 1em;
  }
`

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
`

const RowLabel = styled.label`
  margin: 0;
  cursor: pointer;
  font-weight: normal;
`

/**
 * Six self-describing rows for the datetime branch.
 *
 * Each relative row has a composite key (<condition>__<direction>) that uniquely
 * identifies it even though two rows share the same `condition`. Terminal rows
 * (is_unknown, has_any_value) have no number input.
 */
const SIX_ROWS = [
  {
    key: 'is_before__ago',
    condition: 'is_before',
    direction: 'ago',
    leadingText: 'is more than',
    trailingText: 'days ago',
    hasInput: true,
  },
  {
    key: 'is_on_or_after__ago',
    condition: 'is_on_or_after',
    direction: 'ago',
    leadingText: 'is within the last',
    trailingText: 'days',
    hasInput: true,
  },
  {
    key: 'is_on_or_after__from_now',
    condition: 'is_on_or_after',
    direction: 'from_now',
    leadingText: 'is more than',
    trailingText: 'days from now',
    hasInput: true,
  },
  {
    key: 'is_before__from_now',
    condition: 'is_before',
    direction: 'from_now',
    leadingText: 'is within the next',
    trailingText: 'days',
    hasInput: true,
  },
  {
    key: 'is_unknown',
    condition: 'is_unknown',
    leadingText: 'is unknown',
    hasInput: false,
  },
  {
    key: 'has_any_value',
    condition: 'has_any_value',
    leadingText: 'has any value',
    hasInput: false,
  },
]

/**
 * Decode a stored {condition, value} pair into the composite row key.
 * Falls back to Row 1 (is_before__ago) for any unrecognised input.
 */
function decodeToKey(condition, encodedValue) {
  if (condition === 'is_unknown') {
    return 'is_unknown'
  }
  if (condition === 'has_any_value') {
    return 'has_any_value'
  }
  if (condition === 'is_before' || condition === 'is_on_or_after') {
    const { direction } = parseRelativeDateValue(encodedValue)
    return `${condition}__${direction}`
  }
  return 'is_before__ago'
}

const RELATIVE_ROW_KEYS = SIX_ROWS.filter((r) => r.hasInput).map((r) => r.key)

/**
 * Build the initial per-row days map. Every relative row defaults to '30'; the
 * one row matching a valid stored value decodes to that value's number.
 */
function buildInitialDaysByKey(safeValue) {
  const daysByKey = {}
  RELATIVE_ROW_KEYS.forEach((key) => {
    daysByKey[key] = '30'
  })
  if (isValidRelativeDateValue(safeValue.value)) {
    const decodedKey = decodeToKey(safeValue.condition, safeValue.value)
    if (daysByKey[decodedKey] !== undefined) {
      daysByKey[decodedKey] = parseRelativeDateValue(safeValue.value).days
    }
  }
  return daysByKey
}

/**
 * DatetimeBranch — datetime condition UI (PET-2049 six-row redesign).
 *
 * Renders six self-describing radio rows. The direction (ago / from_now) is
 * baked into each row; there is NO standalone direction Dropdown. The four
 * relative rows each keep their OWN number-of-days value (each defaulting to
 * 30) — editing one row's number changes only that row. Focusing or typing in
 * a row selects it; the emitted rule reflects the selected row's own number.
 * Terminal rows (is_unknown / has_any_value) have no input.
 */
function DatetimeBranch({ value, index, namePrefix, onChange }) {
  const safeValue = value || {}

  const [selectedKey, setSelectedKey] = React.useState(() =>
    decodeToKey(safeValue.condition, safeValue.value)
  )
  // Independent number-of-days value per relative row (keyed by row key).
  const [daysByKey, setDaysByKey] = React.useState(() => buildInitialDaysByKey(safeValue))

  const onChangeRef = React.useRef(onChange)
  React.useLayoutEffect(() => {
    onChangeRef.current = onChange
  })
  const debouncedEmit = React.useRef(null)
  const latest = React.useRef({ key: selectedKey, days: daysByKey[selectedKey] })

  function emitFor(key, daysValue) {
    const row = SIX_ROWS.find((r) => r.key === key)
    if (!row) {
      return
    }
    if (!row.hasInput) {
      onChangeRef.current({ condition: row.condition, value: true })
      return
    }
    onChangeRef.current({
      condition: row.condition,
      value: serializeRelativeDateValue({ days: daysValue, direction: row.direction }),
    })
  }

  function cancelPendingEmit() {
    if (debouncedEmit.current) {
      debouncedEmit.current.cancel()
    }
  }

  // A freshly-added rule has no stored value yet — emit the default for the
  // pre-selected relative row on mount so the rule is immediately valid
  // (parity with the FEAT-879 behaviour).
  React.useEffect(() => {
    const selectedRow = SIX_ROWS.find((r) => r.key === selectedKey)
    if (!isValidRelativeDateValue(safeValue.value) && selectedRow && selectedRow.hasInput) {
      emitFor(selectedKey, daysByKey[selectedKey])
    }
    return cancelPendingEmit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function selectRow(row) {
    cancelPendingEmit()
    setSelectedKey(row.key)
    if (!row.hasInput) {
      latest.current = { key: row.key, days: '' }
      onChangeRef.current({ condition: row.condition, value: true })
    } else {
      const rowDays = daysByKey[row.key]
      latest.current = { key: row.key, days: rowDays }
      emitFor(row.key, rowDays)
    }
  }

  function handleFocus(row) {
    if (row.key !== selectedKey) {
      selectRow(row)
    }
  }

  function handleDaysChange(row, newDays) {
    setDaysByKey((prev) => ({ ...prev, [row.key]: newDays }))
    setSelectedKey(row.key)
    latest.current = { key: row.key, days: newDays }
    if (!debouncedEmit.current) {
      debouncedEmit.current = debounce(() => {
        if (latest.current.days !== '') {
          emitFor(latest.current.key, latest.current.days)
        }
      }, 1000)
    }
    debouncedEmit.current()
  }

  return (
    <Content>
      {SIX_ROWS.map((row) => {
        const radioId = `${namePrefix}datetime-radio-${row.key}-${index}`
        const isSelected = selectedKey === row.key
        return (
          <DateRow key={row.key}>
            <Radio
              id={radioId}
              label={row.leadingText}
              name={`${namePrefix}datetime-rule-radio-${index}`}
              value={row.key}
              checked={isSelected}
              onChange={() => selectRow(row)}
            />
            {row.hasInput && (
              <RelativeDateInput
                value={daysByKey[row.key]}
                onChange={(newDays) => handleDaysChange(row, newDays)}
                onFocus={() => handleFocus(row)}
              />
            )}
            {row.trailingText && <RowLabel htmlFor={radioId}>{row.trailingText}</RowLabel>}
          </DateRow>
        )
      })}
    </Content>
  )
}

DatetimeBranch.propTypes = {
  value: PropTypes.object,
  index: PropTypes.number.isRequired,
  namePrefix: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}

/**
 * AudienceRuleInputBase — the class component containing the render logic.
 * Receives enableRelativeDateAudienceFilter as a prop from the wrapper.
 */
class AudienceRuleInputBase extends React.Component {
  static propTypes = {
    shouldAutoFocus: PropTypes.bool,
    namePrefix: PropTypes.string.isRequired,
    value: PropTypes.object,
    field: PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      field_type: PropTypes.string,
    }).isRequired,
    onDismiss: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    shouldShowAutocomplete: PropTypes.bool,
    shouldEnableFourComparison: PropTypes.bool,
    enableRelativeDateAudienceFilter: PropTypes.bool,
  }

  static defaultProps = {
    namePrefix: '',
    onChange: () => {},
    index: 0,
    enableRelativeDateAudienceFilter: false,
  }

  debouncedInputOnChange = null

  render() {
    const { field, onDismiss, onChange, enableRelativeDateAudienceFilter } = this.props
    const isDatetimeBranch = enableRelativeDateAudienceFilter && field.field_type === 'datetime'
    const value = this.props.value || {
      condition: isDatetimeBranch ? 'is_before' : 'is',
      value: '',
    }

    return (
      <Message className="bg-white" onDismiss={onDismiss}>
        <div style={{ width: '100%' }}>
          <Message.Header className="h4">{field.label}</Message.Header>
          {isDatetimeBranch ? (
            <DatetimeBranch
              value={value}
              index={this.props.index}
              namePrefix={this.props.namePrefix}
              onChange={onChange}
            />
          ) : (
            <State initial={value}>
              {({ state, setState }) => {
                const onRadioChanged = (_, { value: v }) => {
                  const newState = {
                    ...state,
                    condition: v,
                  }
                  setState(newState)
                  onChange(newState)
                }

                const onInputChangedSemanticUi = (_, { value: v }) => {
                  const newState = {
                    ...state,
                    value: v,
                  }
                  setState(newState)

                  if (!this.debouncedInputOnChange) {
                    this.debouncedInputOnChange = debounce((s) => onChange(s), 1000)
                  }
                  this.debouncedInputOnChange(newState)
                }

                const onInputChanged = (v) => {
                  const newState = {
                    ...state,
                    value: v,
                  }
                  setState(newState)

                  if (!this.debouncedInputOnChange) {
                    this.debouncedInputOnChange = debounce((s) => onChange(s), 1000)
                  }
                  this.debouncedInputOnChange(newState)
                }

                const fieldKey = this.props.field.key
                const attribute = this.props.field.isCoreField ? '' : this.props.field.value
                return (
                  <Content>
                    <Radio
                      id={this.props.namePrefix + 'rule-radio-is-' + this.props.index}
                      label="Is:"
                      value="is"
                      name={this.props.namePrefix + 'rule-radio-' + this.props.index}
                      checked={state.condition === 'is'}
                      onChange={onRadioChanged}
                    />
                    {state.condition === 'is' && (
                      <ConditionInput
                        field={fieldKey}
                        attribute={attribute}
                        defaultValue={state.value}
                        onChange={onInputChanged}
                        onChangeSemanticUi={onInputChangedSemanticUi}
                        shouldAutoFocus={this.props.shouldAutoFocus}
                        shouldShowAutocomplete={this.props.shouldShowAutocomplete}
                      />
                    )}
                    <Radio
                      id={this.props.namePrefix + 'rule-radio-is-not-' + this.props.index}
                      label="Is not:"
                      value="is_not"
                      name={'ruleRadio' + this.props.index}
                      checked={state.condition === 'is_not'}
                      onChange={onRadioChanged}
                    />
                    {state.condition === 'is_not' && (
                      <ConditionInput
                        field={fieldKey}
                        attribute={attribute}
                        defaultValue={state.value}
                        onChange={onInputChanged}
                        onChangeSemanticUi={onInputChangedSemanticUi}
                        shouldAutoFocus={this.props.shouldAutoFocus}
                        shouldShowAutocomplete={this.props.shouldShowAutocomplete}
                      />
                    )}
                    {this.props.shouldEnableFourComparison && (
                      <React.Fragment>
                        <Radio
                          label="Contains:"
                          value="contains"
                          name={'ruleRadioContains' + this.props.index}
                          checked={state.condition === 'contains'}
                          onChange={onRadioChanged}
                        />
                        {state.condition === 'contains' && (
                          <AutoFocusSemanticUiComponent
                            functionName={this.props.shouldAutoFocus ? 'focus' : undefined}
                          >
                            <Input
                              required
                              fluid
                              value={state.value}
                              onChange={onInputChangedSemanticUi}
                            />
                          </AutoFocusSemanticUiComponent>
                        )}
                      </React.Fragment>
                    )}
                    {this.props.shouldEnableFourComparison && (
                      <React.Fragment>
                        <Radio
                          label="Does not contain:"
                          value="does_not_contain"
                          name={'ruleRadioNotContain' + this.props.index}
                          checked={state.condition === 'does_not_contain'}
                          onChange={onRadioChanged}
                        />
                        {state.condition === 'does_not_contain' && (
                          <AutoFocusSemanticUiComponent
                            functionName={this.props.shouldAutoFocus ? 'focus' : undefined}
                          >
                            <Input
                              required
                              fluid
                              value={state.value}
                              onChange={onInputChangedSemanticUi}
                            />
                          </AutoFocusSemanticUiComponent>
                        )}
                      </React.Fragment>
                    )}
                  </Content>
                )
              }}
            </State>
          )}
        </div>
      </Message>
    )
  }
}

function ConditionInput({
  field,
  attribute,
  defaultValue,
  onChangeSemanticUi,
  onChange,
  shouldAutoFocus,
  shouldShowAutocomplete,
}) {
  if (fieldsThatShouldUseInput.includes(attribute) || !shouldShowAutocomplete) {
    return <Input required fluid value={defaultValue} onChange={onChangeSemanticUi} />
  }

  return (
    <AudienceRuleAutoCompleteInputContainer
      attribute={attribute}
      field={field}
      defaultValue={defaultValue}
      onChange={onChange}
      shouldAutoFocus={shouldAutoFocus}
    />
  )
}

const fieldsThatShouldUseInput = ['first_name', 'last_name', 'source', 'customer.id', 'ticket.id']

/**
 * AudienceRuleInput — named export used by all call-sites and by the test.
 * Functional component that reads enableRelativeDateAudienceFilter from the
 * feature-toggle hook and passes it as a prop to the class-based renderer.
 * This pattern keeps the class logic unchanged and allows hooks to be used.
 */
export function AudienceRuleInput(props) {
  const { enableRelativeDateAudienceFilter } = useFeatureToggle()
  return (
    <AudienceRuleInputBase
      {...props}
      enableRelativeDateAudienceFilter={enableRelativeDateAudienceFilter}
    />
  )
}

AudienceRuleInput.propTypes = AudienceRuleInputBase.propTypes
AudienceRuleInput.defaultProps = AudienceRuleInputBase.defaultProps

export default AudienceRuleInput



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/AudienceRuleInput/index.js